import { useId, useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { useChargement } from "@/lib/chargement";
import { quantiteLisible } from "@/lib/libelles";
import { api, type LigneDeLaFicheFournisseur } from "@/lib/api";

/**
 * Les gestes d'une ligne de commande fournisseur envoyée (LOT 2, ticket #38,
 * ADR-0045) : elle ne se corrige plus, elle évolue par des gestes motivés. La
 * base borne tout — le reliquat, le motif, le fournisseur actif — et ses refus
 * s'affichent tels quels ; l'écran ne fait que proposer.
 */

/** Suggérés, sans y enfermer : le motif reste du texte libre (décision 16). */
export const MOTIFS_SUGGERES = [
  "Rupture",
  "Délai trop long",
  "Problème fournisseur",
  "Avenant client",
  "Changement de référence",
];

export function ChampMotif({
  valeur,
  onChange,
  requis = true,
}: {
  valeur: string;
  onChange: (valeur: string) => void;
  requis?: boolean;
}) {
  const liste = useId();
  return (
    <>
      <Champ
        libelle="Motif"
        aide="Rupture, délai trop long, avenant client… ou vos propres mots."
        required={requis}
        list={liste}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={liste}>
        {MOTIFS_SUGGERES.map((motif) => (
          <option key={motif} value={motif} />
        ))}
      </datalist>
    </>
  );
}

/** Commandé, reçu, annulé, reliquat : en toutes lettres (story 34). */
export function quantitesEnLettres(ligne: LigneDeLaFicheFournisseur) {
  const en = (quantite: number) => `${quantiteLisible(quantite)} ${ligne.unite}`;
  return [
    `Commandé ${en(ligne.quantite_commandee)}`,
    `reçu ${en(ligne.quantite_recue)}`,
    `annulé ${en(ligne.quantite_annulee)}`,
    ligne.reliquat > 0 ? `reste à recevoir ${en(ligne.reliquat)}` : "plus rien à recevoir",
  ].join(", ");
}

const nombre = (texte: string) => Number(texte.replace(",", "."));

export function FormulaireAnnulationReliquat({
  ligne,
  onAnnuler,
  onValider,
}: {
  ligne: LigneDeLaFicheFournisseur;
  onAnnuler: () => void;
  onValider: (saisie: { quantite: number; motif: string }) => Promise<void>;
}) {
  const [quantite, setQuantite] = useState(String(ligne.reliquat));
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      aria-label={`Annuler le reliquat de ${ligne.designation}`}
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({ quantite: nombre(quantite), motif: motif.trim() });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Quantité annulée"
          aide={
            `Il reste ${quantiteLisible(ligne.reliquat)} ${ligne.unite} à recevoir ;` +
            " ce qui est reçu ne s'annule pas."
          }
          required
          inputMode="decimal"
          value={quantite}
          onChange={(e) => setQuantite(e.target.value)}
        />
        <ChampMotif valeur={motif} onChange={setMotif} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim() || !quantite.trim()}>
          Annuler ce reliquat
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Garder la ligne
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Commander ailleurs : la quantité choisie, au plus le reliquat (proposé par
 * défaut), s'annule ici et se commande chez un autre fournisseur actif, dans
 * une commande neuve en préparation, en une transaction ; le reste demeure
 * attendu ici. La ligne nouvelle sert la même commande client et le même lot.
 */
export function FormulaireCommandeAilleurs({
  ligne,
  fournisseurActuelId,
  avecPrix,
  onAnnuler,
  onValider,
}: {
  ligne: LigneDeLaFicheFournisseur;
  fournisseurActuelId: string;
  avecPrix: boolean;
  onAnnuler: () => void;
  onValider: (saisie: Record<string, unknown>) => Promise<void>;
}) {
  const fournisseurs = useChargement(
    (signal) => api.fournisseurs({ statut: "ACTIF", limite: 200 }, signal),
    [],
  );
  const [fournisseurId, setFournisseurId] = useState("");
  const [designation, setDesignation] = useState(ligne.designation);
  const [reference, setReference] = useState(ligne.reference ?? "");
  const [quantite, setQuantite] = useState(String(ligne.reliquat));
  const [prix, setPrix] = useState("");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);

  if (fournisseurs.erreur) {
    return <Erreur message={fournisseurs.erreur} onReessayer={fournisseurs.recharger} />;
  }
  if (!fournisseurs.donnees) return <Chargement message="Chargement des fournisseurs…" />;
  const autres = fournisseurs.donnees.elements.filter((f) => f.id !== fournisseurActuelId);

  return (
    <form
      aria-label={`Commander ailleurs ${ligne.designation}`}
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        const saisie: Record<string, unknown> = {
          fournisseur_id: fournisseurId,
          motif: motif.trim(),
          designation: designation.trim(),
          reference: reference.trim() || null,
          quantite_commandee: quantite.replace(",", "."),
        };
        if (avecPrix && prix.trim()) saisie.prix_unitaire_ht = prix.replace(",", ".");
        await onValider(saisie);
        setEnvoi(false);
      }}
    >
      <p className="text-sm text-encre-douce">
        Il reste {quantiteLisible(ligne.reliquat)} {ligne.unite} à recevoir. La quantité
        choisie, au plus ce reliquat, s'annule ici et se commande dans une nouvelle commande,
        en préparation, chez le fournisseur choisi ; le reste demeure attendu ici.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ChampSelection
          libelle="Chez"
          required
          options={[
            { valeur: "", libelle: "Choisir un fournisseur" },
            ...autres.map((f) => ({ valeur: f.id, libelle: f.nom })),
          ]}
          value={fournisseurId}
          onChange={(e) => setFournisseurId(e.target.value)}
        />
        <ChampMotif valeur={motif} onChange={setMotif} />
        <Champ
          libelle="Désignation"
          required
          value={designation}
          onChange={(e) => setDesignation(e.target.value)}
        />
        <Champ
          libelle="Référence"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        <Champ
          libelle={`Quantité (${ligne.unite})`}
          required
          inputMode="decimal"
          value={quantite}
          onChange={(e) => setQuantite(e.target.value)}
        />
        {avecPrix ? (
          <Champ
            libelle="Prix unitaire HT"
            inputMode="decimal"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
          />
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Bouton
          type="submit"
          disabled={envoi || !fournisseurId || !motif.trim() || !designation.trim()}
        >
          Commander ailleurs
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Garder la ligne ici
        </Bouton>
      </div>
    </form>
  );
}
